import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  RepairItem,
  RepairFormData,
  Observation,
  PriceModification,
  StatusHistory,
} from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useRepairShopContext } from "@/context/RepairShopContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { repairCache } from "@/utils/cacheManager";
import { usePageVisibility } from "@/hooks/usePageVisibility";

interface RepairContextType {
  repairs: RepairItem[];
  loading: boolean;
  showAllTickets: boolean;
  setShowAllTickets: (show: boolean) => void;
  addRepair: (data: RepairFormData) => Promise<RepairItem>;
  getRepairById: (id: string) => Promise<RepairItem | undefined>;
  getRepairByTicketNumber: (
    ticketNumber: string
  ) => Promise<RepairItem | undefined>;
  updateRepair: (id: string, data: Partial<RepairItem>) => Promise<void>;
  searchRepairs: (query: string) => RepairItem[];
  deleteRepair: (id: string) => Promise<void>;
  importRepairs: (repairs: RepairItem[]) => Promise<void>;
  refreshRepairs: () => Promise<void>;
}

const RepairContext = createContext<RepairContextType | undefined>(undefined);

export const RepairProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [repairs, setRepairs] = useState<RepairItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const { user } = useAuth();
  const { repairShop } = useRepairShopContext();
  const { t } = useTranslation();
  const { isVisible } = usePageVisibility();
  const lastFetchTimeRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);

  // Fetch repairs when user, repair shop, or toggle changes
  useEffect(() => {
    if (user && repairShop) {
      // Try to load from cache first
      const cacheKey = repairCache.generateKey(
        "repairs",
        repairShop.id,
        showAllTickets ? "all" : "assigned"
      );
      const cachedRepairs = repairCache.get<RepairItem[]>(cacheKey);

      if (cachedRepairs && !isInitialLoadRef.current) {
        setRepairs(cachedRepairs);
        setLoading(false);
        console.log("Loaded repairs from cache:", cachedRepairs.length);
      } else {
        // Refresh from database
        refreshRepairs();
      }

      isInitialLoadRef.current = false;

      // Set up real-time subscription for repairs
      const repairsSubscription = supabase
        .channel("repairs-changes")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "repairs",
            filter: `repair_shop_id=eq.${repairShop.id}`,
          },
          (payload) => {
            console.log("Real-time update received:", payload);
            // Clear cache and refresh
            repairCache.clearByPrefix("repairs");
            refreshRepairs();
          }
        )
        .subscribe();

      // Clean up subscription when component unmounts or dependencies change
      return () => {
        supabase.removeChannel(repairsSubscription);
      };
    } else {
      setRepairs([]);
      setLoading(false);
    }
  }, [user, repairShop, showAllTickets]);

  const refreshRepairs = async () => {
    if (!user || !repairShop) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("repairs")
        .select("*, repair_shop:repair_shop_id(*)")
        .eq("repair_shop_id", repairShop.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching repairs:", error);
        throw error;
      }

      console.log("Fetched repairs count:", data?.length);

      // Convert string dates to Date objects and ensure correct types
      const processedRepairs = data.map((repair: any) => ({
        id: repair.id,
        customerName: repair.customer_name,
        customerPhone: repair.customer_phone,
        phoneModel: repair.phone_model,
        problemDescription: repair.problem_description,
        repairPrice: repair.repair_price,
        paymentStatus: repair.payment_status as "paid" | "partial" | "unpaid",
        downPayment: repair.down_payment,
        createdAt: new Date(repair.created_at),
        completedAt: repair.completed_at
          ? new Date(repair.completed_at)
          : undefined,
        status: repair.status as
          | "pending"
          | "inProgress"
          | "completed"
          | "cancelled"
          | "returned",
        userId: repair.user_id,
        repairShopId: repair.repair_shop_id,
        ticketNumber: repair.ticket_number,
        assignedTechnician: repair.assigned_technician || undefined,
        isAssignedToCurrentUser: repair.assigned_technician === user.id,
        repairShop: repair.repair_shop
          ? {
              id: repair.repair_shop.id,
              name: repair.repair_shop.name,
              address: repair.repair_shop.address,
              phone: repair.repair_shop.phone,
              createdAt: new Date(repair.repair_shop.created_at),
            }
          : undefined,
        observations: (() => {
          try {
            if (repair.observations && repair.observations.trim() !== "") {
              return JSON.parse(repair.observations).map((obs: any) => ({
                id: obs.id,
                text: obs.text,
                createdAt: new Date(obs.created_at),
                userId: obs.user_id,
              }));
            }
          } catch (error) {
            console.error("Error parsing observations JSON:", error);
          }
          return [];
        })(),
        priceModifications: (() => {
          try {
            if (
              repair.price_modifications &&
              repair.price_modifications.trim() !== ""
            ) {
              return JSON.parse(repair.price_modifications).map((mod: any) => ({
                id: mod.id,
                amount: mod.amount,
                reason: mod.reason,
                createdAt: new Date(mod.created_at),
                userId: mod.user_id,
              }));
            }
          } catch (error) {
            console.error("Error parsing price modifications JSON:", error);
          }
          return [];
        })(),
      }));

      setRepairs(processedRepairs);
      lastFetchTimeRef.current = Date.now();

      // Cache the results
      const cacheKey = repairCache.generateKey(
        "repairs",
        repairShop.id,
        showAllTickets ? "all" : "assigned"
      );
      repairCache.set(cacheKey, processedRepairs);

      console.log(
        "Fetched and cached repairs from database:",
        processedRepairs.length
      );
    } catch (error) {
      console.error("Error fetching repairs:", error);
      toast.error(t("repair.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const addRepair = async (data: RepairFormData): Promise<RepairItem> => {
    if (!user) throw new Error("User must be logged in");
    if (!repairShop) throw new Error("No repair shop available");

    try {
      const newRepair = {
        customer_name: data.customerName,
        customer_phone: data.customerPhone,
        phone_model: data.phoneModel,
        problem_description: data.problemDescription,
        repair_price: data.repairPrice,
        payment_status: data.paymentStatus,
        down_payment: data.downPayment,
        status: "pending" as const,
        user_id: user.id,
        repair_shop_id: repairShop.id,
        ...(data.customerEmail && { customer_email: data.customerEmail }),
        ...(data.assignedTechnician && {
          assigned_technician: data.assignedTechnician,
        }),
        observations: (() => {
          try {
            if (data.observations && data.observations.length > 0) {
              return JSON.stringify(
                data.observations.map((text) => ({
                  id: crypto.randomUUID(),
                  text,
                  created_at: new Date().toISOString(),
                  user_id: user.id,
                }))
              );
            }
          } catch (error) {
            console.error("Error stringifying observations during add:", error);
          }
          return "[]";
        })(),
        price_modifications: "[]",
        ...(() => {
          try {
            return {
              status_history: JSON.stringify([
                {
                  id: crypto.randomUUID(),
                  status: "pending",
                  created_at: new Date().toISOString(),
                  user_id: user.id,
                },
              ]),
            };
          } catch (error) {
            console.log("Status history column might not exist yet, skipping");
            return {};
          }
        })(),
      };

      const { data: insertedData, error } = await supabase
        .from("repairs")
        .insert(newRepair)
        .select("*, repair_shop:repair_shop_id(*)")
        .single();

      if (error) {
        throw error;
      }

      // Save custom form response if provided
      if (
        data.customFormResponses?.templateId &&
        data.customFormResponses?.responses
      ) {
        try {
          await supabase.from("form_responses").insert({
            repair_id: insertedData.id,
            template_id: data.customFormResponses.templateId,
            responses: data.customFormResponses.responses,
            repair_shop_id: repairShop.id,
          });
        } catch (formError) {
          console.error("Error saving custom form response:", formError);
        }
      }

      // Save stock items and update quantities if provided
      if (data.stockItems && data.stockItems.length > 0) {
        try {
          for (const item of data.stockItems) {
            const { data: product, error: productError } = await supabase
              .from("products")
              .select("price, stock_quantity")
              .eq("id", item.id)
              .single();

            if (
              productError ||
              !product ||
              product.stock_quantity < item.quantity
            ) {
              console.error("Product issue:", item.id);
              continue;
            }

            await supabase.from("repair_stock_items").insert({
              repair_id: insertedData.id,
              product_id: item.id,
              quantity: item.quantity,
              unit_price: product.price,
              repair_shop_id: repairShop.id,
            });

            await supabase
              .from("products")
              .update({
                stock_quantity: product.stock_quantity - item.quantity,
              })
              .eq("id", item.id);

            await supabase.from("stock_movements").insert({
              product_id: item.id,
              type: "out",
              quantity: item.quantity,
              reason: `Used in repair #${insertedData.ticket_number}`,
              reference: insertedData.id,
              user_id: user.id,
              repair_shop_id: repairShop.id,
            });
          }
        } catch (stockError) {
          console.error("Error processing stock items:", stockError);
        }
      }

      // Convert to our app's RepairItem format
      const processedRepair: RepairItem = {
        id: insertedData.id,
        customerName: insertedData.customer_name,
        customerPhone: insertedData.customer_phone,
        customerEmail: insertedData.customer_email || undefined,
        assignedTechnician: insertedData.assigned_technician || undefined,
        phoneModel: insertedData.phone_model,
        problemDescription: insertedData.problem_description,
        repairPrice: insertedData.repair_price,
        paymentStatus: insertedData.payment_status as
          | "paid"
          | "partial"
          | "unpaid",
        downPayment: insertedData.down_payment,
        createdAt: new Date(insertedData.created_at),
        completedAt: insertedData.completed_at
          ? new Date(insertedData.completed_at)
          : undefined,
        status: insertedData.status as
          | "pending"
          | "inProgress"
          | "completed"
          | "cancelled"
          | "returned",
        userId: insertedData.user_id,
        repairShopId: insertedData.repair_shop_id,
        ticketNumber: insertedData.ticket_number,
        repairShop: insertedData.repair_shop
          ? {
              id: insertedData.repair_shop.id,
              name: insertedData.repair_shop.name,
              address: insertedData.repair_shop.address,
              phone: insertedData.repair_shop.phone,
              createdAt: new Date(insertedData.repair_shop.created_at),
            }
          : undefined,
        observations: (() => {
          try {
            if (
              insertedData.observations &&
              insertedData.observations.trim() !== ""
            ) {
              return JSON.parse(insertedData.observations).map((obs: any) => ({
                id: obs.id,
                text: obs.text,
                createdAt: new Date(obs.created_at),
                userId: obs.user_id,
              }));
            }
          } catch (error) {
            console.error("Error parsing observations JSON:", error);
          }
          return [];
        })(),
        statusHistory: (() => {
          try {
            if (
              insertedData.status_history &&
              insertedData.status_history.trim() !== ""
            ) {
              return JSON.parse(insertedData.status_history).map(
                (entry: any) => ({
                  id: entry.id,
                  status: entry.status,
                  createdAt: new Date(entry.created_at),
                  userId: entry.user_id,
                })
              );
            }
          } catch (error) {
            console.error("Error parsing status history JSON:", error);
          }
          return [];
        })(),
      };

      setRepairs([processedRepair, ...repairs]);

      // Clear cache after adding repair
      repairCache.clearByPrefix("repairs");

      return processedRepair;
    } catch (error) {
      console.error("Error adding repair:", error);
      toast.error(t("repair.addError"));
      throw error;
    }
  };

  const getRepairById = async (id: string): Promise<RepairItem | undefined> => {
    if (!user) return undefined;

    try {
      const { data, error } = await supabase
        .from("repairs")
        .select("*, repair_shop:repair_shop_id(*)")
        .eq("id", id)
        .single();

      if (error || !data) return undefined;

      return {
        id: data.id,
        customerName: data.customer_name,
        customerPhone: data.customer_phone,
        customerEmail: data.customer_email || undefined,
        phoneModel: data.phone_model,
        problemDescription: data.problem_description,
        repairPrice: data.repair_price,
        paymentStatus: data.payment_status as "paid" | "partial" | "unpaid",
        downPayment: data.down_payment,
        createdAt: new Date(data.created_at),
        completedAt: data.completed_at
          ? new Date(data.completed_at)
          : undefined,
        status: data.status as
          | "pending"
          | "inProgress"
          | "completed"
          | "cancelled"
          | "returned",
        userId: data.user_id,
        repairShopId: data.repair_shop_id,
        ticketNumber: data.ticket_number,
        assignedTechnician: data.assigned_technician || undefined,
        repairShop: data.repair_shop
          ? {
              id: data.repair_shop.id,
              name: data.repair_shop.name,
              address: data.repair_shop.address,
              phone: data.repair_shop.phone,
              createdAt: new Date(data.repair_shop.created_at),
            }
          : undefined,
        observations: (() => {
          try {
            if (data.observations && data.observations.trim() !== "") {
              return JSON.parse(data.observations).map((obs: any) => ({
                id: obs.id,
                text: obs.text,
                createdAt: new Date(obs.createdAt || obs.created_at),
                userId: obs.userId || obs.user_id,
              }));
            }
          } catch (error) {
            console.error("Error parsing observations JSON:", error);
          }
          return [];
        })(),
        priceModifications: (() => {
          try {
            if (
              data.price_modifications &&
              data.price_modifications.trim() !== ""
            ) {
              return JSON.parse(data.price_modifications).map((mod: any) => ({
                id: mod.id,
                amount: mod.amount,
                reason: mod.reason,
                createdAt: new Date(mod.createdAt || mod.created_at),
                userId: mod.userId || mod.user_id,
              }));
            }
          } catch (error) {
            console.error("Error parsing price modifications JSON:", error);
          }
          return [];
        })(),
        statusHistory: (() => {
          try {
            if (data.status_history && data.status_history.trim() !== "") {
              return JSON.parse(data.status_history).map((entry: any) => ({
                id: entry.id,
                status: entry.status,
                createdAt: new Date(entry.createdAt || entry.created_at),
                userId: entry.userId || entry.user_id,
              }));
            }
          } catch (error) {
            console.error("Error parsing status history JSON:", error);
          }
          return [];
        })(),
      };
    } catch (error) {
      console.error("Error fetching repair:", error);
      return undefined;
    }
  };

  const getRepairByTicketNumber = async (
    ticketNumber: string
  ): Promise<RepairItem | undefined> => {
    if (!user || !repairShop) return undefined;

    try {
      const { data, error } = await supabase
        .from("repairs")
        .select("*, repair_shop:repair_shop_id(*)")
        .eq("repair_shop_id", repairShop.id)
        .eq("ticket_number", ticketNumber)
        .single();

      if (error || !data) return undefined;

      return {
        id: data.id,
        customerName: data.customer_name,
        customerPhone: data.customer_phone,
        customerEmail: data.customer_email || undefined,
        phoneModel: data.phone_model,
        problemDescription: data.problem_description,
        repairPrice: data.repair_price,
        paymentStatus: data.payment_status as "paid" | "partial" | "unpaid",
        downPayment: data.down_payment,
        createdAt: new Date(data.created_at),
        completedAt: data.completed_at
          ? new Date(data.completed_at)
          : undefined,
        status: data.status as
          | "pending"
          | "inProgress"
          | "completed"
          | "cancelled"
          | "returned",
        userId: data.user_id,
        repairShopId: data.repair_shop_id,
        ticketNumber: data.ticket_number,
        assignedTechnician: data.assigned_technician || undefined,
        repairShop: data.repair_shop
          ? {
              id: data.repair_shop.id,
              name: data.repair_shop.name,
              address: data.repair_shop.address,
              phone: data.repair_shop.phone,
              createdAt: new Date(data.repair_shop.created_at),
            }
          : undefined,
        observations: [],
        priceModifications: [],
        statusHistory: [],
      };
    } catch (error) {
      console.error("Error fetching repair by ticket:", error);
      return undefined;
    }
  };

  const updateRepair = async (
    id: string,
    data: Partial<RepairItem>
  ): Promise<void> => {
    if (!user) return;

    try {
      const dbData: any = {};
      if (data.customerName !== undefined)
        dbData.customer_name = data.customerName;
      if (data.customerPhone !== undefined)
        dbData.customer_phone = data.customerPhone;
      if (data.customerEmail !== undefined)
        dbData.customer_email = data.customerEmail;
      if (data.phoneModel !== undefined) dbData.phone_model = data.phoneModel;
      if (data.problemDescription !== undefined)
        dbData.problem_description = data.problemDescription;
      if (data.repairPrice !== undefined)
        dbData.repair_price = data.repairPrice;
      if (data.paymentStatus !== undefined)
        dbData.payment_status = data.paymentStatus;
      if (data.downPayment !== undefined)
        dbData.down_payment = data.downPayment;
      if (data.status !== undefined) {
        dbData.status = data.status;
        // Add to status history if it exists
        if (data.statusHistory) {
          try {
            dbData.status_history = JSON.stringify(
              data.statusHistory.map((entry) => ({
                id: entry.id,
                status: entry.status,
                created_at: entry.createdAt.toISOString(),
                user_id: entry.userId,
              }))
            );
          } catch (error) {
            console.error("Error stringifying status history:", error);
          }
        }
      }
      if (data.completedAt !== undefined)
        dbData.completed_at = data.completedAt;
      if (data.observations !== undefined) {
        dbData.observations = JSON.stringify(
          data.observations.map((obs) => ({
            id: obs.id,
            text: obs.text,
            createdAt: obs.createdAt.toISOString(),
            userId: obs.userId,
          }))
        );
      }
      if (data.priceModifications !== undefined) {
        dbData.price_modifications = JSON.stringify(
          data.priceModifications.map((mod) => ({
            id: mod.id,
            amount: mod.amount,
            reason: mod.reason,
            createdAt: mod.createdAt.toISOString(),
            userId: mod.userId,
          }))
        );
      }

      const { error } = await supabase
        .from("repairs")
        .update(dbData)
        .eq("id", id);

      if (error) throw error;

      setRepairs(
        repairs.map((repair) =>
          repair.id === id ? { ...repair, ...data } : repair
        )
      );

      // Clear cache after updating repair
      repairCache.clearByPrefix("repairs");
    } catch (error) {
      console.error("Error updating repair:", error);
      toast.error(t("repair.updateError"));
    }
  };

  const searchRepairs = (query: string): RepairItem[] => {
    const lowercaseQuery = query.toLowerCase();
    return repairs.filter(
      (repair) =>
        repair.customerName.toLowerCase().includes(lowercaseQuery) ||
        repair.customerPhone.includes(lowercaseQuery) ||
        repair.phoneModel.toLowerCase().includes(lowercaseQuery)
    );
  };

  const deleteRepair = async (id: string): Promise<void> => {
    if (!user) return;

    try {
      const { error } = await supabase.from("repairs").delete().eq("id", id);

      if (error) {
        throw error;
      }

      setRepairs(repairs.filter((repair) => repair.id !== id));

      // Clear cache after deleting repair
      repairCache.clearByPrefix("repairs");
    } catch (error) {
      console.error("Error deleting repair:", error);
      toast.error(t("repair.deleteError"));
    }
  };

  const importRepairs = async (newRepairs: RepairItem[]): Promise<void> => {
    if (!user || !repairShop) return;

    try {
      const dbRepairs = newRepairs.map((repair) => ({
        customer_name: repair.customerName,
        customer_phone: repair.customerPhone,
        customer_email: repair.customerEmail,
        phone_model: repair.phoneModel,
        problem_description: repair.problemDescription,
        repair_price: repair.repairPrice,
        payment_status: repair.paymentStatus,
        down_payment: repair.downPayment,
        status: repair.status,
        created_at: repair.createdAt.toISOString(),
        completed_at: repair.completedAt?.toISOString(),
        user_id: user.id,
        repair_shop_id: repairShop.id,
        ticket_number: repair.ticketNumber,
      }));

      const { error } = await supabase.from("repairs").insert(dbRepairs);
      if (error) throw error;

      // Clear cache before refreshing
      repairCache.clearByPrefix("repairs");
      await refreshRepairs();
    } catch (error) {
      console.error("Error importing repairs:", error);
      toast.error(t("repair.importError"));
    }
  };

  return (
    <RepairContext.Provider
      value={{
        repairs,
        loading,
        addRepair,
        getRepairById,
        getRepairByTicketNumber,
        updateRepair,
        searchRepairs,
        deleteRepair,
        importRepairs,
        refreshRepairs,
        showAllTickets,
        setShowAllTickets,
      }}
    >
      {children}
    </RepairContext.Provider>
  );
};

export const useRepairContext = () => {
  const context = useContext(RepairContext);
  if (context === undefined) {
    throw new Error("useRepairContext must be used within a RepairProvider");
  }
  return context;
};
